<template>
  <content-subpage
    class="acp-image-selection-subpage"
    :visible="visible"
    :titles="titles"
    :content-title="t('acp_image_selection_subpage_title')"
    :back-label="`${t('content_subpage_go_back_link')} ${parentSubpageName}`"
    :has-parent-subpage="props.params.hasParentSubpage"
    @content-subpage-hidden="actionHidden"
    @content-subpage-back="handleBack"
  >
    <template #main-content>
      <overlay-loader v-if="isLoading" />
      <template v-else>
        <!-- Description -->
        <div class="description">{{ t("acp_image_selection_subpage_description") }}</div>

        <div class="generated-image-container">
          <!-- AI Info Notice -->
          <cv-inline-notification kind="info" low-contrast hide-close-button>
            <template #subtitle>
              <div>{{ t("acp_image_selection_subpage_info_message") }}</div>
            </template>
          </cv-inline-notification>

          <div v-if="generatedImageList.length > 0" class="generated-image-list">
            <div v-for="image in generatedImageList" :key="image.fileUuid" class="generated-image">
              <input-radio
                :id="image.fileUuid"
                v-model:selected-value="selectedImage"
                kind="white"
                :value="image.fileUuid"
              ></input-radio>
              <img :src="populateImageFileUrl(image)" />
            </div>
          </div>
          <empty-placeholder v-else>
            <template #icon>
              <GenericEmpty />
            </template>
            {{ t("acp_image_selection_subpage_empty_placeholder_label") }}
          </empty-placeholder>
        </div>
      </template>
    </template>

    <template v-if="showAddButton" #action-button>
      <fetch-data-button
        class="btn-add"
        tip-position="top"
        tip-alignment="end"
        :icon-only="false"
        :disabled="addButton.disabled"
        :loading="addButton.loading"
        :error="addButton.error"
        :error-message="addButton.errorMessage"
        @click="handleAddButton"
        @reset-error-state="handleResetErrorState"
      >
        <span v-if="addButton.error">{{ t("button_state_error") }}</span>
        <template v-else>
          <svg-icon name="Plus24" kind="action" />
          <span>{{ t("button_add") }}</span>
        </template>
      </fetch-data-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed, onBeforeMount, onMounted, ref } from "vue";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import GenericEmpty from "@images/placeholder/empty-states/GenericEmpty.svg";
import MediaObjectApi from "@apis/media.object.api.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import FileUploadApi from "@apis/file.upload.api.ts";
import {
  pollScanningStatus,
  getSuccessFileUrl,
} from "@plugins/file-uploader-v2/composables/useFileUploader.ts";
import type { AcpGeneratedImageFile } from "@plugins/authoring-copilot/types.ts";

interface GeneratedImage {
  fileUuid: string;
  fileName: string;
}

interface ResourceContext {
  resourceUuid: string;
  resourceType: string;
  uploadFrom: string;
  resourceDomain: string;
  ownerGroupId: number;
}

interface ContentSubpageParams {
  id: string;
  contextId: string;
  baseTitle?: string[];
  hasParentSubpage?: boolean;
  requestId: string;
  richTextContext: ResourceContext;
  files: string[];
}

interface Props {
  params: ContentSubpageParams;
}

const props = defineProps<Props>();

const emit = defineEmits(["image-added"]);

const {
  visible,
  titles,
  setVisible,
  actionHiddenCallBack,
  actionBackCallBack,
  actionHidden,
  actionBack,
  isComponentDirty,
  removeComponentDirty,
  promptComponentDirty,
} = useContentSubpage(props.params);

const { t } = useI18n();
const modal = useModal();
const errorHandler = useErrorHandler();

const parentSubpageName = ref("");
const isLoading = ref(false);

const generatedImageList = ref<GeneratedImage[]>([]);

const selectedImage = ref();

const addButton = ref({
  error: false,
  errorMessage: "",
  loading: false,
  completed: false,
  disabled: false,
});

const showAddButton = computed(() => {
  return generatedImageList.value.length > 0;
});

const getImages = async () => {
  isLoading.value = true;
  for (const fileUuid of props.params.files) {
    try {
      const scanResultResponse = await pollScanningStatus(fileUuid);

      if (
        scanResultResponse.code === "ok" &&
        scanResultResponse.data &&
        scanResultResponse.data.status == "READY"
      ) {
        generatedImageList.value.push({
          fileUuid: fileUuid,
          fileName: scanResultResponse.data.fileName,
        });
      }
    } catch (e) {
      console.log(e);
    }
  }

  selectedImage.value = generatedImageList.value[0]?.fileUuid;
  isLoading.value = false;
};

const populateImageFileUrl = (image: GeneratedImage) => {
  return getSuccessFileUrl(image.fileUuid, image.fileName);
};

const handleBack = () => {
  modal.showMessage("info", {
    title: t("acp_image_selection_subpage_back_confirmation_title"),
    content: t("acp_image_selection_subpage_back_confirmation_message"),
    primaryBtnLabel: t("button_proceed"),
    secondaryBtnLabel: t("button_cancel"),
    actionPrimary: () => {
      actionBack();
    },
  });
};

const handleAddButton = async () => {
  addButton.value.loading = true;

  try {
    const fileUuid = selectedImage.value;
    const context = props.params.richTextContext;
    const image = generatedImageList.value.find((img) => img["fileUuid"] === selectedImage.value);
    const fileName = image.fileName;

    let data;
    if (context) {
      const imageMoModel = {
        requestId: props.params.requestId,
        contextUuid: context.resourceUuid,
        contextType: context.resourceType,
        uploadFrom: context.uploadFrom,
        fileName: fileName,
        resourceDomain: context.resourceDomain,
        ownerGroupId: context.ownerGroupId,
        fileUuid: selectedImage.value,
      };
      const response = await MediaObjectApi.createImageMediaObject({ ...imageMoModel });
      data = { ...response.data, type: "mo" };
    } else {
      const blob = await getImageBlob(fileUuid, fileName);
      const generatedImageFile = new File([blob], fileName, {
        type: blob.type,
      }) as AcpGeneratedImageFile;
      generatedImageFile.fileUuid = fileUuid;
      generatedImageFile.acpRequestId = props.params.requestId;
      data = generatedImageFile;
    }

    addButton.value.loading = false;
    addButton.value.completed = true;

    emit("image-added", data);

    actionHidden();
  } catch {
    addButton.value.loading = false;
    addButton.value.error = true;
  }
};

const handleResetErrorState = () => {
  addButton.value.error = false;
  addButton.value.errorMessage = "";
};

const getImageBlob = async (fileUuid, fileName) => {
  try {
    const blob = await FileUploadApi.getSuccessFileBlob(`${fileUuid}/${fileName}`);
    return blob;
  } catch {
    errorHandler.passiveApiFailHandler();
  }
};

onMounted(async () => {
  await getImages();
});

onBeforeMount(async () => {
  if (props.params.hasParentSubpage) {
    const len = titles.value.length;
    parentSubpageName.value = titles.value[len - 1] || "";
  }
  titles.value.push(t("acp_image_selection_subpage_title"));
});

defineExpose({
  params: props.params,
  setVisible,
  actionHiddenCallBack,
  actionBackCallBack,
  isComponentDirty,
  removeComponentDirty,
  promptComponentDirty,
});
</script>

<style scoped lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.acp-image-selection-subpage {
  .generated-image-container {
    margin: $spacing-05 (-$spacing-05) 0;
    padding: $spacing-05;
    background-color: $ui-01;

    .bx--inline-notification {
      margin-top: 0;
    }
  }

  .generated-image-list {
    display: grid;
    gap: $spacing-05;
    background-color: $ui-01;

    @include media("tablet-min") {
      grid-template-columns: repeat(2, 1fr);
    }

    @include media("desktop") {
      padding: $spacing-05;
      border: 1px solid $ui-03;
      border-radius: 10px;
    }
  }

  .generated-image {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: start;
    padding: $spacing-05;
    background-color: $ui-background;
    border: 1px solid $ui-03;
    border-radius: 10px;

    .input-radio {
      margin-top: 0;
    }

    img {
      max-width: 100%;
    }
  }
}
</style>
